import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { InputField, Modal, SelectField, Button } from "..";
import { colors, css, fonts, getGraphColor } from "../../Styles";
import { FieldStyles, FieldTypes, ListData_ValueTypes, TableStyles_Types, ButtonTypes } from "../../Definitions";
import { useEditableColumnConstructor } from "../Tables/_Tables/useEditableColumnConstructor";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../Tables/reactiveTableRenderer";
import Graph_QL from "../../Services/Agrisys_GqlDataCaller";
import { useAppSelector } from "../../Store";
import { FeedCurveInfo } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/GQLCalls/getFeedCurvesCall";
import { useFeedCurves } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";
import { useFeedCurveSettings } from "../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurveSettings";
import { keyBy, merge, noop } from "lodash";

const getComponentTableDivStyle = (id?: string | number):CSSProperties|undefined => {
  if (!id) { return }
  return {backgroundColor: getGraphColor(+id), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}
}
interface props {
  isModalOpen: boolean
  closeModal: () => void,
  feedCurveNo: number
  // feedCurveComponentIds: number[]
}

interface DirtyData {
  component_id: number,
  feedcurve_id: number,
  gram_per_minute?: number
}

export default function FeedCurveSettingsModal({isModalOpen, closeModal, feedCurveNo}: props) {
  const {t} = useTranslation()
  const [showFeedCurve, setShowFeedCurve] = useState<FeedCurveInfo | undefined>(undefined);
  const dirtRef = useRef<DirtyData[]>([])


  // Connection
  const farm = useAppSelector(({activeData}) => activeData.farm)
  const domain = useMemo(() => (Graph_QL.ObjToString({domain: farm ?? ""})),[farm]);
  const connection = useMemo(() => (Graph_QL.create({domain: farm ?? ""})),[farm]);
 
  // Find label in select field options corresponding to their respective ID
  const findLabelById = (id: number, options: { label: string, id: number }[]) => {
    const option = options.find(option => option.id === id);
    return option ? option.label : "";
  };

  // #region | Feed Curves data
  const {call: getFeedCurvesInfo, data: feedCurvesState } = useFeedCurves(connection);
  const {call: getFeedCurveSettings, maintainEntries: maintainCurveSettings, data: feedCurveSettingsState } = useFeedCurveSettings(connection);
  
  const feedCurvesData = useMemo(() => {
    return feedCurvesState && feedCurvesState[domain] ? feedCurvesState[domain].data : [];
  },[domain, feedCurvesState])

  useEffect(() => {
    if (!feedCurveSettingsState[domain] || !feedCurveSettingsState[domain].data.find((setting) => (setting.feedcurve_id ===feedCurveNo)))
      {
        getFeedCurveSettings(connection, feedCurveNo);
      }
  },[connection, domain, feedCurveNo, feedCurveSettingsState, getFeedCurveSettings])

  useEffect(() => {
    if (!feedCurvesState[domain]) {
      getFeedCurvesInfo(connection, {ids: [feedCurveNo]})
    }
  },[connection, domain, feedCurveNo, feedCurvesState, getFeedCurvesInfo])

  useEffect(()=> {
    setShowFeedCurve(feedCurvesData.find((feedCurve) => feedCurve.id === feedCurveNo))
  },[feedCurvesData, feedCurveNo])
  // #endregion

  // #region | Remaining Feed
  const [remainingFeed, setRemainingFeed] = useState<number>(0)

  const remainingFeedOptions = useMemo(() => ([    
    { label: t("feed.settings.doNotPass"), id: 0 },
    { label: t("feed.settings.pass"), id: 1 },
  ]),[t])
  // #endregion

  // #region | Stimoli
  const [stimoli, setStimoli] = useState<number>(0)
  const [stimoliRepeat, setStimoliRepeat] = useState<number>(0)

  const stimoliOptions = useMemo(() => ([    
    { label: t("feed.settings.noStimoli"), id: 0 },
    { label: t("feed.settings.addStimoli"), id: 1 },
  ]),[t])

  const stimoliRepeatOptions = useMemo(() => ([
    { label: t("feed.settings.singleStimoli"), id: 0 },
    { label: t("feed.settings.repeatStimoli"), id: 1 }, 
  ]),[t])
  // #endregion

  // #region | Components Table
  const accessorData = useMemo(() => ([
    {headerValue: "", accessor: "component", type: ListData_ValueTypes.Div, staticColumn: true, divStyleOverride: getComponentTableDivStyle, styleOverrideOnValue: false},
    {headerValue: "", accessor: "feedSpeed", type: ListData_ValueTypes.Number}
  ]),[])
  
  const feedCurveSettingsData = useMemo(() => (feedCurveSettingsState[domain]?.data.filter((setting) => setting.feedcurve_id===feedCurveNo)),[domain, feedCurveNo, feedCurveSettingsState])
  const columns = useEditableColumnConstructor({accessorData, tableStyle: TableStyles_Types.tertiaryTable, canEdit: true });

  const table = useReactTable({
    columns,
    data: feedCurveSettingsData ?? [],
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    meta: {
      updateData: (component_id, columnId, value) => {
        dirtRef.current = (Object.values(merge(keyBy(dirtRef.current, "component_id"), keyBy([{component_id, [columnId === "feedSpeed" ? "gram_per_minute" : columnId ]: value ? +value: undefined, feedcurve_id: feedCurveNo}], "component_id"))))
      },
      deleteRow: noop
    }
  })

  // #endregion

  return (
    <Modal 
      modalHeader={t("settings")}
      modalSubHeader={t("feed.feedPlan.feedCurve")+` ${showFeedCurve?.id}: ${showFeedCurve?.name}`}
      isModalOpen={isModalOpen}
      overrides={{
        isPrimaryComponent: true,
      }}
      onCloseBtn={() => {closeModal(); dirtRef.current=[]}}
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "40px", marginTop: "10px"}}>
          
        <div style={{ width: "600px" }}>
          <div style={{
            borderTop: "2px solid " + colors.blueDarkSecondary,
            borderBottom: "2px solid " + colors.blueDarkSecondary,
            padding: "10px 10px",
            maxHeight: "50vh",
            overflowY: "scroll",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: colors.blueDarkSecondary+colors.transparent,
            display: "flex",
            alignItems: "center",
            justifyContent: "start",
            flexDirection: "column",
            gap: "10px"
          }}>

            {/* Components / Feed Speed */}
            <div style={{marginBottom: "20px", width: "100%"}}>
            <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.settings.feedSpeed")}</p>
              {ReactiveTable({table, tableType: TableStyles_Types.tertiaryTable, emptyText: t("feed.settings.noComponents"), renderFooter: false})}
            </div>

            {/* Max Periode Amount */}
            <div style={{marginBottom: "20px", width: "100%", display: "flex", flexDirection: "column"}}>
              <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.settings.maxAmount")}</p>
              <InputField 
                fieldType={FieldTypes.primaryInputField} 
                fieldStyle={FieldStyles.blueDarkSecondary} 
                textStart={true} 
                placeholder={t("units.kg")+"..."}
              />
            </div>

            {/* Remaining Feed */}
            <div style={{marginBottom: "20px", width: "100%", display: "flex", flexDirection: "column"}}>
              <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.settings.remaining")}</p>
              <SelectField 
                defaultValue={findLabelById(remainingFeed, remainingFeedOptions)}
                options={remainingFeedOptions}
                onChange={([selectedOption]) => selectedOption && setRemainingFeed(selectedOption.id as number)}
                overrides={{
                  fieldType: FieldTypes.primaryInputField,
                  fieldStyle: FieldStyles.blueDarkSecondary,
                  textStart: true
                }}
              />
              { remainingFeed === 1 && (
                <div style={{ width: "calc(100% - 20px)", height: "fit-content", gap: "5px", alignItems: "center", backgroundColor: colors.greyLightSecondary, padding: "10px 10px", borderRadius: "10px", marginTop: "10px", display: "flex", flexDirection: "row" }}>
                  <InputField 
                    fieldType={FieldTypes.shortInputField} 
                    fieldStyle={FieldStyles.greyTertiary} 
                    textStart={true} 
                    placeholder={t("units.kg")+"..."}
                  />
                  <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{t("feed.settings.toPass")}</p>
                </div> 
              )}
            </div>

            {/* Feed Curve Stimoli */}
            <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
              <p style={{ ...fonts.header, ...fonts.textShadow, ...fonts.fontSize4, color: colors.greyLightTertiary, padding: "0px 0px 2px 22px" }}>{t("feed.settings.stimoli")}</p>
              <SelectField 
                defaultValue={findLabelById(stimoli, stimoliOptions)}
                options={stimoliOptions}
                onChange={([selectedOption]) => selectedOption && setStimoli(selectedOption.id as number)}
                overrides={{
                  fieldType: FieldTypes.primaryInputField,
                  fieldStyle: FieldStyles.blueDarkSecondary,
                  textStart: true
                }}
              />
              { stimoli === 1 && (
                <div style={{ width: "calc(100% - 20px)", height: "fit-content", backgroundColor: colors.greyLightSecondary, padding: "10px 10px", borderRadius: "10px", marginTop: "10px", display: "flex", flexDirection: "row", gap: "10px" }}>
                  <SelectField 
                    overrides={{
                      fieldType: FieldTypes.primaryInputField,
                      fieldStyle: FieldStyles.greyTertiary, 
                      textStart: true
                    }}
                    defaultValue={findLabelById(stimoliRepeat, stimoliRepeatOptions)}
                    options={stimoliRepeatOptions}
                    onChange={([selectedOption]) => selectedOption && setStimoliRepeat(selectedOption.id as number)}    
                  />
                  { stimoliRepeat === 1 && (
                    <div style={{display: "flex", flexDirection: "row", gap: "5px", alignItems: "center"}}>
                      <InputField 
                        fieldType={FieldTypes.shortInputField} 
                        fieldStyle={FieldStyles.greyTertiary} 
                        textStart={true}
                      />
                      <p style={{...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary}}>{t("feed.settings.minutesApart")}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </div>
        <Button content={t("buttons.saveBtn")} buttonType={ButtonTypes.primaryButton} 
          overrides={{
            baseButton: {justifyContent: "center"},
            hoverStyles: {backgroundColor: colors.blackPrimary}
          }}
          onClick={() => {
            if (maintainCurveSettings) {maintainCurveSettings({data: dirtRef.current});}
            dirtRef.current=[]
          }}
        />
      </div>
    </Modal>
  );
}